import "moment/locale/vi";
import "moment/locale/en-au";

import { AxiosStatic } from "axios";
import { CssBaseline } from "@mui/material";
import { createContext, useContext, useEffect } from "react";
import { DefaultTheme as Theme } from "@mui/system";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";

import theme from "../assets/theme";
import Global from "../commons/global";
import moment from "moment";
import Strings from "../constants/strings";
import { setLocale } from "yup";

import { Permission } from "../constants/enums";
import { Constants } from "../constants/constants";
import { MaterialUIControllerProvider } from "../components/context";
import { AlertType, ICodename, IResourcePemssions, IUserInfo } from "../commons/interfaces";

import "../styles/muiIcon.css";
import "../styles/main.css";


export interface ICommonComponentContextType {
    axios: AxiosStatic;
    userInfo?: IUserInfo;
    language: string;
    clientId: string;
    tenantCode: string;
    serviceCode: string;
    organizationId?: string;
    listMultiLanguage: ICodename[];
    aesKey?: string;
    aesIV?: string;

    // Danh sách khóa màn hình UI
    dataMapScreenName?: { [key: string]: JSX.Element };

    onShowLoading?: () => void;
    onHideLoading?: () => void;
    onSuccess?: (msg: string) => void;
    onError?: (msg: string, type?: AlertType) => void;
    theme?: Partial<Theme> | ((outerTheme: Theme) => Theme);
}

export const CommonComponentContext = createContext<
    ICommonComponentContextType & {
        getResourcePermissions: (resourceUri: string) => IResourcePemssions;
    }
>(null!);

export const useCommonComponentContext = () => useContext(CommonComponentContext);

export interface ICommonComponentProviderProps {
    value: ICommonComponentContextType;
    children: React.ReactNode;
}

export const CommonComponentProvider = (props: ICommonComponentProviderProps) => {
    Global.axios = props.value.axios;

    useEffect(() => {
        moment.locale(props?.value?.language);
        Strings.setLanguage(props?.value?.language);

        setLocale({
            string: {
                email: Strings.getInstance().Common.INVALID_EMAIL,
            },
            mixed: {
                required: Strings.Common.REQUIRED,
            },
        });
    }, [props?.value?.language]);

    const getResourcePermissions = (resourceURI: string): IResourcePemssions => {
        const permission = (props.value?.userInfo?.resourceCodes || []).find((el) => el.resourceURI === resourceURI)?.permission || 0;
        return {
            canRead: hasPermission(permission, Permission.Read),
            canListOrExport: hasPermission(permission, Permission.ListOrExport),
            canCreate: hasPermission(permission, Permission.Create),
            canBulkCreateOrImport: hasPermission(permission, Permission.BulkCreateOrImport),
            canUpdate: hasPermission(permission, Permission.Update),
            canBulkUpdate: hasPermission(permission, Permission.BulkUpdate),
            canDelete: hasPermission(permission, Permission.Delete),
            canBulkDelete: hasPermission(permission, Permission.BulkDelete),
        };
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={props.value.theme || theme}>
                <MaterialUIControllerProvider>
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                        dateFormats={{ keyboardDate: "DD/MM/YYYY" }}
                        adapterLocale={props.value?.language === Constants.DefaultLanguage ? "vi" : "en-au"}
                    >
                        <CommonComponentContext.Provider
                            value={{
                                ...props.value,
                                getResourcePermissions,
                            }}
                        >
                            <CssBaseline />
                            {props.children}
                        </CommonComponentContext.Provider>
                    </LocalizationProvider>
                </MaterialUIControllerProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

const hasPermission = (permissionValues: number, permission: Permission) => (permissionValues & permission) === permission;
